// eslint-disable-next-line
import './apolloMemoryLimits';

import { ApolloClient, InMemoryCache } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { ApolloLink, split } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { WebSocketLink } from 'apollo-link-ws';
import { createUploadLink } from 'apollo-upload-client';

import browserHistory from './history';
import { signOut } from './user';

/**
 This changes allows us to be able to track the progress of file uploads.
 It is a workaround for the issue with apollo-upload-client not supporting file upload progress tracking.
 https://github.com/jaydenseric/apollo-upload-client/issues/88
 */
const parseHeaders = (rawHeaders: string): Headers => {
  const headers = new Headers();
  const preProcessedHeaders = rawHeaders.replace(/\r?\n[\t ]+/g, ' ');
  preProcessedHeaders.split(/\r?\n/).forEach((line: string) => {
    const parts = line.split(':');
    const key = parts.shift()?.trim();
    if (key) {
      const value = parts.join(':').trim();
      headers.append(key, value);
    }
  });
  return headers;
};

type OnloadOptions = { status: number; statusText: string; headers: Headers } & Record<string, any>;
type CustomFetchOptions = RequestInit & {
  onProgress?: (ev: ProgressEvent) => void;
};

export const uploadFetch = (
  url: URL | RequestInfo,
  options: CustomFetchOptions,
): Promise<Response> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const opts: OnloadOptions = {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: parseHeaders(xhr.getAllResponseHeaders() || ''),
      };
      opts.url = 'responseURL' in xhr ? xhr.responseURL : opts.headers.get('X-Request-URL');
      const body = 'response' in xhr ? xhr.response : (xhr as any).responseText;
      resolve(new Response(body, opts));
    };
    xhr.onerror = () => {
      reject(new TypeError('Network request failed'));
    };
    xhr.ontimeout = () => {
      reject(new TypeError('Network request failed'));
    };
    xhr.open(options.method || '', url as string, true);
    xhr.withCredentials = true;
    const rawHeaders =
      options.headers instanceof Headers
        ? Object.fromEntries(options.headers.entries())
        : ((options.headers || {}) as Record<string, string>);
    const headersToSend =
      options.body instanceof FormData
        ? Object.keys(rawHeaders).reduce(
            (acc, key) => {
              if (key.toLowerCase() !== 'content-type') {
                acc[key] = rawHeaders[key];
              }
              return acc;
            },
            {} as Record<string, string>,
          )
        : rawHeaders;
    Object.keys(headersToSend).forEach((key) => {
      xhr.setRequestHeader(key, headersToSend[key]);
    });
    if (xhr.upload && options.onProgress) {
      xhr.upload.onprogress = options.onProgress;
    }
    xhr.send(options.body as XMLHttpRequestBodyInit | Document | null | undefined);
  });

export const customFetch = (
  uri: URL | RequestInfo,
  options: CustomFetchOptions = {},
): Promise<Response> => {
  if (options.onProgress) {
    return uploadFetch(uri, options);
  }
  return fetch(uri, options);
};

const uploadLink = createUploadLink({
  uri: process.env.REACT_APP_API,
  credentials: 'include',
  fetch: customFetch,
});

const wsLink = new WebSocketLink({
  uri: process.env.REACT_APP_WS_API!,
  options: {
    reconnect: true,
    lazy: true,
  },
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  //@ts-expect-error - Argument of type 'ApolloLink' is not assignable to parameter of type 'ApolloLink | RequestHandler | undefined'.
  uploadLink,
);

const apolloClient = new ApolloClient({
  //@ts-expect-error - Type 'ApolloLink' is missing the following properties from type 'ApolloLink': onError, setOnError
  link: ApolloLink.from([
    onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path, extensions }) => {
          console.log(
            `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
              locations,
            )}, Path: ${JSON.stringify(path)}, Extension: ${extensions?.code}`,
          );
          if (extensions && extensions.code === 'INTERNAL_SERVER_ERROR') {
            browserHistory.push('/error');
          }
        });
      }
      if (networkError) {
        console.log(`[Network error]: ${networkError}`);
        browserHistory.push('/error');
      }
      if (graphQLErrors) {
        graphQLErrors.forEach((graphQLError) => {
          if (graphQLError.extensions && graphQLError.extensions.code === 'UNAUTHENTICATED') {
            signOut();
          }
        });
      }
    }),
    splitLink,
  ]),
  cache: new InMemoryCache(),
});

export default apolloClient;
